/**
 * Композбл ресайзабельной панели с drag-handle.
 *
 * Используется для чата справа от формы сущности (НС, Заявка и др.) —
 * пользователь тянет разделитель между формой и чатом мышкой, чат меняет
 * ширину. Значение сохраняется в localStorage по ключу `storageKey`, чтобы
 * восстанавливаться при повторном открытии карточки.
 */
import { onBeforeUnmount, ref, watch } from 'vue'

export interface IUseResizablePaneParams {
  /** Начальная ширина панели в px (используется если в localStorage ничего нет). */
  initial: number
  /** Минимально допустимая ширина в px. */
  min: number
  /** Максимально допустимая ширина в px (либо функция для динамического расчёта). */
  max: number | (() => number)
  /** Ключ localStorage для сохранения ширины. Без префиксов — добавится автоматически. */
  storageKey?: string
}

export interface IUseResizablePaneReturn {
  /** Текущая ширина панели в px. */
  width: ReturnType<typeof ref<number>>
  /** Навешивается на mousedown разделителя — запускает drag. */
  startResize: (event: MouseEvent) => void
  /** Флаг активного перетаскивания (полезно для курсора/оверлея в шаблоне). */
  isResizing: ReturnType<typeof ref<boolean>>
}

const LS_PREFIX = 'materio.pane-width.'

function resolveMax(max: number | (() => number)): number {
  return typeof max === 'function' ? max() : max
}

function readFromStorage(key: string | undefined): number | null {
  if (!key) return null
  try {
    const raw = localStorage.getItem(LS_PREFIX + key)
    if (raw == null) return null
    const n = Number(raw)
    return Number.isFinite(n) ? n : null
  } catch {
    return null
  }
}

function writeToStorage(key: string | undefined, value: number): void {
  if (!key) return
  try {
    localStorage.setItem(LS_PREFIX + key, String(Math.round(value)))
  } catch {
    // localStorage недоступен — молча игнорируем
  }
}

export function useResizablePane(params: IUseResizablePaneParams): IUseResizablePaneReturn {
  const clamp = (v: number) => {
    const maxV = resolveMax(params.max)
    return Math.min(Math.max(v, params.min), maxV)
  }

  const initial = readFromStorage(params.storageKey) ?? params.initial
  const width = ref<number>(clamp(initial))
  const isResizing = ref(false)

  /** Сохраняем изменения ширины в localStorage (debounce не нужен — writes редкие). */
  watch(width, v => {
    if (v != null) writeToStorage(params.storageKey, v)
  })

  let startX = 0
  let startWidth = 0

  const onMouseMove = (e: MouseEvent) => {
    // Двигаем правую панель: тянем разделитель влево — панель шире,
    // вправо — уже. delta инвертируем относительно стартовой ширины.
    const delta = startX - e.clientX
    width.value = clamp(startWidth + delta)
  }

  const onMouseUp = () => {
    isResizing.value = false
    document.removeEventListener('mousemove', onMouseMove)
    document.removeEventListener('mouseup', onMouseUp)
    document.body.style.userSelect = ''
    document.body.style.cursor = ''
  }

  const startResize = (event: MouseEvent) => {
    event.preventDefault()
    startX = event.clientX
    startWidth = width.value ?? params.initial
    isResizing.value = true
    document.body.style.userSelect = 'none'
    document.body.style.cursor = 'col-resize'
    document.addEventListener('mousemove', onMouseMove)
    document.addEventListener('mouseup', onMouseUp)
  }

  onBeforeUnmount(() => {
    document.removeEventListener('mousemove', onMouseMove)
    document.removeEventListener('mouseup', onMouseUp)
  })

  return { width, startResize, isResizing }
}
